import { AsyncPipe, DatePipe, NgClass, NgIf, NgOptimizedImage } from '@angular/common';
import { Component, ElementRef, OnInit, inject, signal, viewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { LocalUserDto, LocalUsersApi, SetupUserPasswordDto, VerifyLocalUserEmailDto } from '@buspro/web/apis';
import {
  ErrorDisplayComponent,
  ExtendedProblemDetail,
  PasswordValidatorService,
  WEB_CONFIG,
  WebAppEnvironment,
  convertToProblemDetail,
  syncAutofilledInputs,
  useFormErrorHandler,
} from '@buspro/web/core';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzResultComponent } from 'ng-zorro-antd/result';
import { NzSkeletonComponent } from 'ng-zorro-antd/skeleton';
import { NzSpinComponent } from 'ng-zorro-antd/spin';
import { NgxLoadingModule } from 'ngx-loading';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, lastValueFrom } from 'rxjs';

type EmailVerificationStep = 'pending' | 'verifying' | 'verified' | 'error';

@Component({
  selector: 'admin-activate-local',
  templateUrl: './activate-local-account.component.html',
  styleUrls: ['./activate-local-account.component.scss'],
  imports: [
    NgOptimizedImage,
    NgIf,
    AsyncPipe,
    NzAlertComponent,
    ReactiveFormsModule,
    NzResultComponent,
    RouterLink,
    DatePipe,
    NzInputDirective,
    NgxLoadingModule,
    NzSkeletonComponent,
    NgClass,
    ErrorDisplayComponent,
    NzSpinComponent,
  ],
  standalone: true,
})
export class ActivateLocalAccountComponent implements OnInit {
  private fb = inject(FormBuilder);
  private logger = inject(NGXLogger);
  private localUsersApi = inject(LocalUsersApi);
  protected readonly environment = inject<WebAppEnvironment>(WEB_CONFIG);
  private router = inject(Router);

  submitted$$ = new BehaviorSubject<number>(0);
  submitted = false;

  // Password requirements from feature flags
  passwordRequirements = this.getPasswordRequirements();
  passwordHint = PasswordValidatorService.getHintMessage(this.passwordRequirements);

  private getPasswordRequirements() {
    const strictValidationEnabled = this.environment.features?.strictPasswordValidation ?? true;

    if (!strictValidationEnabled) {
      return {
        minLength: 3,
        requireUppercase: false,
        requireLowercase: false,
        requireNumber: false,
        requireSpecialChar: false,
        enforced: false,
      };
    }

    return {
      minLength: 8,
      requireUppercase: true,
      requireLowercase: true,
      requireNumber: true,
      requireSpecialChar: true,
      enforced: true,
    };
  }

  form = this.fb.group({
    email: [{ value: '', disabled: true }, [Validators.required]],
    password: ['', [Validators.required, PasswordValidatorService.createValidator(this.passwordRequirements)]],
    passwordConfirm: ['', [Validators.required]],
  });

  private accountFormRef = viewChild<ElementRef<HTMLFormElement>>('accountForm');

  user$ = new BehaviorSubject<LocalUserDto | null>(null);

  websiteUrl = this.environment.publicWebsiteUrl;
  date = new Date();

  activationToken = signal<string | null>(null);
  isSubmitting = signal<boolean>(false);
  emailVerificationStep = signal<EmailVerificationStep>('pending');
  isPasswordVisible = signal<boolean>(false);

  // Error handling
  showError = signal(false);
  showActivationError = signal(false);
  activationErrorMessage = signal<string>('');
  activationErrorDetails: ExtendedProblemDetail | null = null;
  errorDetails: ExtendedProblemDetail | null = null;

  private readonly formErrorHandler = useFormErrorHandler();

  // Password validation UI
  passwordStrength = signal<number>(0);
  passwordFeedback = signal<string>('');

  query = injectQuery(() => ({
    enabled: this.activationToken() !== null,
    queryKey: ['activation-details', this.activationToken()],
    queryFn: () =>
      lastValueFrom(
        this.localUsersApi.localUsersControllerGetLocalUserDetailsFromActivationToken({
          token: this.activationToken()!,
        }),
      ),
    select: (data: LocalUserDto) => {
      this.logger.log('Local user activation data:', data);
      this.form.get('email')?.setValue(data.email);
      this.user$.next(data);
      return data;
    },
    throwOnError: (error, _query) => {
      this.notification.error('Failed to load activation details', error.message);
      throw new Error(error.message);
    },
    retry: false,
  }));

  constructor(
    private route: ActivatedRoute,
    public notification: NzNotificationService,
  ) {}

  ngOnInit(): void {
    const { actkn } = this.route.snapshot.queryParams;

    this.logger.log('Getting activation details with token', actkn);

    if (actkn) {
      this.activationToken.set(actkn);

      // Step 1: Verify email automatically (controlled by feature flag)
      const emailVerificationRequired = this.environment.features?.emailVerificationRequired ?? true;

      if (emailVerificationRequired) {
        this.verifyEmail(actkn);
      } else {
        // Email verification disabled via feature flag - skip verification
        this.logger.log('Email verification disabled via feature flag - skipping verification');
        this.emailVerificationStep.set('verified');
      }
    }

    // Monitor password changes for strength feedback
    this.form.get('password')?.valueChanges.subscribe((password) => {
      if (password) {
        const strength = this.calculatePasswordStrength(password);
        this.passwordStrength.set(strength);
        this.passwordFeedback.set(this.getPasswordFeedback(password, strength));
      }
    });
  }

  public verifyEmail(token: string): void {
    this.emailVerificationStep.set('verifying');

    const payload: VerifyLocalUserEmailDto = { token };

    this.localUsersApi.localUsersControllerVerifyEmail({ body: payload }).subscribe({
      next: () => {
        this.logger.log('Email verified successfully');
        this.emailVerificationStep.set('verified');
      },
      error: (error: unknown) => {
        this.logger.error('Email verification failed', error);
        this.emailVerificationStep.set('error');

        const result = this.formErrorHandler.handleFormError(error, {
          formName: 'admin-activate-local-verify-email',
        });
        this.showError.set(true);
        this.activationErrorDetails = result.problemDetail ?? convertToProblemDetail(error);
      },
    });
  }

  completeAccountSetup() {
    this.clearErrors();

    // Adopt anything the browser filled in before judging the form — a value
    // assigned straight onto an input never reaches the control.
    syncAutofilledInputs(this.form, this.accountFormRef()?.nativeElement);

    // Validate form
    if (this.form.invalid) {
      this.markFormAsTouched();
      return;
    }

    // Check password match
    if (this.form.get('password')?.value !== this.form.get('passwordConfirm')?.value) {
      this.notification.error('Passwords do not match', 'Please check your password');
      return;
    }

    this.isSubmitting.set(true);

    const payload: SetupUserPasswordDto = {
      userId: this.user$.getValue()!.id,
      password: this.form.get('password')!.value!,
    };

    this.logger.log('Completing account setup...');

    this.localUsersApi.localUsersControllerCompleteUserAccountSetup({ body: payload }).subscribe({
      next: () => {
        this.logger.log('✅ Account setup completed successfully');
        this.submitted$$.next(1);
        this.submitted = true;
        this.isSubmitting.set(false);

        // Redirect to login after 3 seconds
        setTimeout(() => {
          this.router.navigate(['/passport/login']);
        }, 3000);
      },
      error: (error: unknown) => {
        this.logger.error('Account setup failed', error);
        this.isSubmitting.set(false);
        const result = this.formErrorHandler.handleFormError(error, {
          formName: 'admin-activate-local-account',
        });
        if (!result.shouldHandleGlobally) {
          this.showError.set(true);
          this.errorDetails = result.problemDetail ?? convertToProblemDetail(error);
        }
      },
    });
  }

  onRetry(): void {
    this.clearErrors();
    this.completeAccountSetup();
  }

  onDismissError(): void {
    this.clearErrors();
  }

  private clearErrors(): void {
    this.showError.set(false);
    this.errorDetails = null;
  }

  private markFormAsTouched(): void {
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      control?.markAsTouched();
    });
  }

  getPasswordStrengthClass(): string {
    const strength = this.passwordStrength();
    if (strength >= 80) return 'text-success';
    if (strength >= 60) return 'text-warning';
    return 'text-error';
  }

  getPasswordStrengthLabel(): string {
    const strength = this.passwordStrength();
    if (strength >= 80) return 'Strong';
    if (strength >= 60) return 'Medium';
    if (strength >= 40) return 'Weak';
    return 'Very Weak';
  }

  retryActivation(): void {
    this.showActivationError.set(false);
    this.activationErrorMessage.set('');

    const actkn = this.route.snapshot.queryParams['actkn'];
    if (actkn) {
      this.activationToken.set(actkn);

      const emailVerificationRequired = this.environment.features?.emailVerificationRequired ?? true;
      if (emailVerificationRequired) {
        this.verifyEmail(actkn);
      } else {
        this.emailVerificationStep.set('verified');
      }
    }
  }

  private calculatePasswordStrength(password: string): number {
    let strength = 0;

    // Length
    strength += Math.min(password.length * 4, 40);

    // Uppercase
    if (/[A-Z]/.test(password)) strength += 15;

    // Lowercase
    if (/[a-z]/.test(password)) strength += 15;

    // Numbers
    if (/\d/.test(password)) strength += 15;

    // Special chars
    if (/[@$!%*?&]/.test(password)) strength += 15;

    return Math.min(strength, 100);
  }

  private getPasswordFeedback(_password: string, strength: number): string {
    if (strength >= 80) return 'Excellent password!';
    if (strength >= 60) return 'Good password, consider adding more variety';
    if (strength >= 40) return 'Weak password, add more characters and variety';
    return 'Very weak password, please strengthen it';
  }
}
